import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { Box, Grid, TextField, Typography, Checkbox, Button, Paper, Tooltip, IconButton } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import DropdownMenu from '../../../components/DropdownMenu';
import { isoToInput, inputToIso } from 'helpers/helpers';

const AUTO_REGEN_OPTIONS = [
  { name: 'Disabled', value: 0 },
  { name: '1 month', value: 30 },
  { name: '2 months', value: 60 },
  { name: '3 months', value: 90 },
  { name: '6 months', value: 180 },
  { name: '12 months', value: 360 },
];

const formatDisplayValue = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleString();
  }
  return value;
};

const buildFormState = data => {
  const meta = data ?? {};
  return {
    ...meta,
    auto_regeneration_days: Number.isFinite(meta.auto_regeneration_days) ? meta.auto_regeneration_days : 0,
    valid_from: isoToInput(meta.valid_from),
    valid_until: meta.valid_until ?? '',
  };
};

const buildPayloadFromForm = formState => ({
  ...formState,
  valid_from: inputToIso(formState.valid_from),
  valid_until: formState.valid_until ? DateTime.fromISO(formState.valid_until).toISO() : null,
});

const ScheduleMetaDataForm = forwardRef(
  (
    //props
    {
      initialData,
      onChange,
      isRecommendation,
      currentDeviceIds = [],
      originalDeviceIds = [],
      scheduleId,
      onActivationSuccess,
      onActivationError,
    },
    ref,
  ) => {
    const [form, setForm] = useState(() => buildFormState(initialData));
    const { control, setValue } = useForm({
      defaultValues: {
        auto_regeneration_days: Number.isFinite(initialData?.auto_regeneration_days)
          ? initialData.auto_regeneration_days
          : 0,
      },
    });
    const dispatch = useDispatch();
    const [isActivating, setIsActivating] = useState(false);

    useEffect(() => {
      const nextForm = buildFormState(initialData);
      setForm(nextForm);
      setValue('auto_regeneration_days', nextForm.auto_regeneration_days);
    }, [initialData, setValue]);

    const handleChange = e => {
      const { name, value } = e.target;
      setForm(prev => ({ ...prev, [name]: value }));
      onChange?.();
    };

    const handleAutoRegenerationChange = value => {
      const parsedValue = Number(value);
      const nextValue = Number.isNaN(parsedValue) ? 0 : parsedValue;
      setForm(prev => ({
        ...prev,
        auto_regeneration_days: nextValue,
      }));
      onChange?.();
      return nextValue;
    };

    // TODO: NAAMA sync with DB and backend times. test everything
    const handleAutoRegenerationStartChange = value => {
      const nextValue = value?.startOf('day')?.toISO() ?? '';
      setForm(prev => ({ ...prev, valid_until: nextValue }));
      onChange?.();
    };

    // const handleStatusChange = async e => {
    //   e.preventDefault();

    //   setIsActivating(true);

    //   try {
    //     // DEACTIVATION FLOW
    //     if (form.status === 'active' || form.status === 'active_not_synced') {

    //       onActivationSuccess?.('Schedule deactivated successfully');

    //       // Update form state to reflect deactivation
    //       setForm(prev => ({
    //         ...prev,
    //         status: 'pending',
    //       }));

    //       return; // Exit after deactivation
    //     }

    //     // ACTIVATION FLOW
    //     // Determine if devices have changed by comparing arrays
    //     const devicesChanged =
    //       currentDeviceIds.length !== originalDeviceIds.length ||
    //       !currentDeviceIds.every(id => originalDeviceIds.includes(id));

    //     if (devicesChanged || !isRecommendation) {
    //       // Devices were modified - use activateScheduleForDevices
    //       const cleanDeviceIds = [...new Set(currentDeviceIds.map(id => String(id)))];

    //       await dispatch(
    //         activateScheduleForDevices({
    //           scheduleId: scheduleId,
    //           devicesIds: cleanDeviceIds,
    //         }),
    //       ).unwrap();

    //       onActivationSuccess?.(`Schedule activated on ${cleanDeviceIds.length} device(s)`);
    //     } else {
    //       // Devices unchanged - use activateScheduleByScheduleId (for recommendations)
    //       await dispatch(
    //         activateScheduleByScheduleId({
    //           scheduleId: scheduleId,
    //         }),
    //       ).unwrap();

    //       onActivationSuccess?.('Schedule activated successfully');
    //     }

    //     // Update form state to reflect activation
    //     setForm(prev => ({
    //       ...prev,
    //       status: 'active',
    //     }));
    //   } catch (error) {
    //     onActivationError?.(error.message || 'Failed to update schedule status');
    //   } finally {
    //     setIsActivating(false);
    //   }
    // };

    useImperativeHandle(ref, () => ({
      getFormData: () => buildPayloadFromForm(form),
    }));

    return (
      <>
        {/* <Typography variant="h4" gutterBottom sx={{}}>
        Schedule Properties
      </Typography> */}
        <Grid container spacing={1} py={1.5} sx={{ alignItems: 'flex-start' }}>
          <Grid item xs={12} py={1.5}>
            <TextField
              label="Schedule Name"
              name="schedule_name"
              // disabled={isRecommendation}
              value={form.schedule_name ?? ''}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          {isRecommendation && (
            <>
              <Grid item xs={12} py={1.5}>
                <Controller
                  name="auto_regeneration_days"
                  control={control}
                  render={({ field: { onChange: fieldOnChange, value } }) => (
                    <DropdownMenu
                      options={AUTO_REGEN_OPTIONS}
                      selectedOption={Number.isFinite(value) ? value : 0}
                      handleChange={event => {
                        const nextValue = handleAutoRegenerationChange(event.target.value);
                        fieldOnChange(nextValue);
                      }}
                      labelName="Auto Regeneration Days:"
                      inline={true}
                      labelWidth="fit-content"
                      emptyLabel="Disabled"
                      width="15rem"
                      info="Choose how often the recommendation schedule should regenerate automatically."
                    />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={12}
                py={1.5}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex' }}>
                  <Typography sx={{ width: 'fit-content', whiteSpace: 'nowrap' }}>
                    Auto Regeneration Interval Start:
                  </Typography>
                  <Tooltip
                    title="Pick the date the automatic regeneration intervals should start from."
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 300,
                        },
                      },
                    }}>
                    <IconButton size="small" aria-label={`Smart info`} onClick={event => event.stopPropagation()}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <DatePicker
                    label="Start date"
                    value={form.valid_until ? DateTime.fromISO(form.valid_until) : null}
                    onChange={handleAutoRegenerationStartChange}
                    minDate={DateTime.local().startOf('day')}
                    format="dd.MM.yyyy"
                    textField={params => <TextField {...params} fullWidth />}
                    sx={{ width: '15.5rem' }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        InputLabelProps: {
                          sx: { lineHeight: 1.4 }, // try 1.3–1.6
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}

          {/* Show Generated Timestamp only for non-recommendations */}
          {!isRecommendation &&
            [{ label: 'Creation time', name: 'generated_timestamp' }].map(field => (
              <Grid item xs={12} py={1.5} key={field.name}>
                <Box
                  sx={{
                    border: theme => `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    px: 2,
                    backgroundColor: 'background.default',
                  }}>
                  <Typography variant="caption" color="text.secondary">
                    {field.label}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0 }}>
                    {formatDisplayValue(form[field.name])}
                  </Typography>
                </Box>
              </Grid>
            ))}

          {/* Show Prediction Period only for recommendations */}
          {/* {isRecommendation &&
            [
              { label: 'Period Start', name: 'based_on_period_start' },
              { label: 'Period End', name: 'based_on_period_end' },
            ].map(field => (
              <Grid item xs={12} py={1.5} key={field.name}>
                <Box
                  sx={{
                    border: theme => `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    px: 2,
                    backgroundColor: 'background.default',
                  }}>
                  <Typography variant="caption" color="text.secondary">
                    {field.label}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0 }}>
                    {formatDisplayValue(form[field.name])}
                  </Typography>
                </Box>
              </Grid>
            ))} */}
          {/* {[
          { label: 'Valid From', name: 'valid_from' },
          { label: 'Valid Until', name: 'valid_until' },
        ].map(field => (
          <Grid item xs={12} py={1.5} key={field.name}>
            <TextField
              label={field.label}
              name={field.name}
              type="datetime-local"
              value={form[field.name] ?? ''}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid> */}
        </Grid>
      </>
    );
  },
);

ScheduleMetaDataForm.displayName = 'ScheduleMetaDataForm';

export default ScheduleMetaDataForm;
